/**
 * Feature Gates initialization for DTAC
 * Following Studio's pattern from @atlassian/studio-foundations-feature-gates
 */
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	type ClientOptions,
	type Identifiers,
	FeatureGateEnvironment,
	PerimeterType,
} from '@atlaskit/feature-gate-js-client/types';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags/setBooleanFeatureFlagResolver';

import { getConfig } from '../config';

const TARGET_APP = 'twg-super-app_web'; // https://hello.help.atlassian.cloud/servicedesk/customer/portal/3450/MHD-16936?created=true

/**
 * Get the feature gate environment based on the current config environment
 */
const getFeatureGateEnvironment = (): FeatureGateEnvironment => {
	const env = getConfig().env;
	if (env === 'production') {
		return FeatureGateEnvironment.Production;
	}
	return FeatureGateEnvironment.Staging;
};

/**
 * Feature gate client options
 */
export const featureGatesOptions: ClientOptions = {
	apiKey: getConfig().featureGateApiKey,
	environment: getFeatureGateEnvironment(),
	targetApp: TARGET_APP,
	perimeter: PerimeterType.COMMERCIAL,
};

let initialized = false;

/**
 * Initialize the feature gates client and patch fg() to use Statsig values.
 * Should be called after user profile is loaded.
 *
 * @param accountId - The user's Atlassian account ID for targeting
 */
export const initializeFeatureGates = async (accountId: string): Promise<void> => {
	if (initialized) {
		return;
	}

	const identifiers: Identifiers = {
		atlassianAccountId: accountId,
	};

	try {
		await FeatureGates.initialize(featureGatesOptions, identifiers);

		// Patch fg() from @atlaskit/platform-feature-flags to use Statsig gate values
		setBooleanFeatureFlagResolver((gateName) => FeatureGates.checkGate(gateName));

		initialized = true;
		// eslint-disable-next-line no-console
		console.debug('[FeatureGates] Initialized successfully');
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('[FeatureGates] Failed to initialize:', error);
		// Don't throw - allow app to continue with default values
	}
};
